import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Box,
  LinearProgress,
  Chip,
  Skeleton,
  Alert
} from '@mui/material';
import {
  PictureAsPdf,
  Description,
  PlayArrow,
  Schedule
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { getRecentDocuments } from '../../services/documentStorageService';
import { getDocumentProgress } from '../../services/progressService';
import { useNavigate } from 'react-router-dom';

const RecentDocuments = ({ onOpenDocument, maxItems = 5 }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recentDocs, setRecentDocs] = useState([]);
  const [documentProgress, setDocumentProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadRecentDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { documents } = await getRecentDocuments(user, maxItems);
      setRecentDocs(documents);

      const progressMap = {};
      for (const doc of documents) {
        try {
          const { data: progress } = await getDocumentProgress(doc.document_hash, user);
          if (progress) {
            progressMap[doc.id] = progress;
          }
        } catch (error) {
          console.warn(`Failed to load progress for document ${doc.id}:`, error);
        }
      }
      setDocumentProgress(progressMap);
    } catch (error) {
      console.error('Error loading recent documents:', error);
      setError('Failed to load recent documents');
    } finally {
      setLoading(false);
    }
  }, [user, maxItems]);

  useEffect(() => {
    if (user) {
      loadRecentDocuments();
    } else {
      setLoading(false);
    }
  }, [user, loadRecentDocuments]);

  const handleOpenDocument = useCallback(async (document) => {
    try {
      navigate(`/reader?docId=${document.id}`);
      loadRecentDocuments();
    } catch (error) {
      console.error('Error opening document:', error);
      setError('Failed to open document');
    }
  }, [navigate, loadRecentDocuments]);

  const getFileIcon = (fileType) => {
    if (fileType === 'application/pdf') {
      return <PictureAsPdf sx={{ color: '#d32f2f' }} />;
    }
    return <Description sx={{ color: '#1976d2' }} />;
  };

  const getProgressInfo = (document) => {
    const progress = documentProgress[document.id];
    if (!progress) {
      return { percentage: 0, text: 'Not started' };
    }

    if (document.file_type === 'application/pdf') {
      return {
        percentage: progress.progress_percentage || 0,
        text: `Page ${progress.current_page || 1} of ${progress.total_pages || 1}`
      };
    } else {
      return {
        percentage: progress.progress_percentage || 0,
        text: `${Math.round(progress.progress_percentage || 0)}% read`
      };
    }
  };

  const getLastOpenedText = (lastOpened) => {
    try {
      return formatDistanceToNow(new Date(lastOpened), { addSuffix: true });
    } catch (error) {
      return 'Unknown';
    }
  };

  if (!user) {
    return null; // Don't show for anonymous users
  }

  if (loading) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Recent Documents
        </Typography>
        <List>
          {[...Array(3)].map((_, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <Skeleton variant="circular" width={24} height={24} />
              </ListItemIcon>
              <ListItemText
                primary={<Skeleton variant="text" width="60%" />}
                secondary={<Skeleton variant="text" width="40%" />}
              />
            </ListItem>
          ))}
        </List>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Recent Documents
        </Typography>
        <Alert severity="error" size="small">
          {error}
        </Alert>
      </Paper>
    );
  }

  if (recentDocs.length === 0) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Recent Documents
        </Typography>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Schedule sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No recent documents
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Recent Documents
      </Typography>
      <List dense>
        {recentDocs.map((document) => {
          const progressInfo = getProgressInfo(document);
          
          return (
            <ListItem
              key={document.id}
              role="button"
              tabIndex={0}
              onClick={() => handleOpenDocument(document)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleOpenDocument(document);
                }
              }}
              sx={{
                borderRadius: 1,
                mb: 1,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <ListItemIcon>
                {getFileIcon(document.file_type)}
              </ListItemIcon>
              
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '200px'
                      }}
                    >
                      {document.original_filename}
                    </Typography>
                    {document.is_favorite && (
                      <Chip label="★" size="small" color="error" sx={{ minWidth: 'auto', height: 20 }} />
                    )}
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {getLastOpenedText(document.last_opened)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <LinearProgress
                        variant="determinate"
                        value={progressInfo.percentage}
                        sx={{ flexGrow: 1, height: 4, borderRadius: 2 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 'fit-content' }}>
                        {progressInfo.text}
                      </Typography>
                    </Box>
                  </Box>
                }
              />
              
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDocument(document);
                  }}
                  sx={{ color: 'primary.main' }}
                >
                  <PlayArrow />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );
};

export default RecentDocuments;
